<script lang="ts" setup>
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import CaseCreateEntitiesForm from '@/components/app/cases/CaseCreateEntitiesForm.vue'
  import CaseCreateEntities from '@/components/app/cases/CaseCreateEntities.vue'
  import ClientRelationshipReplacementModal from '@/components/app/cases/entities/ClientRelationshipReplacementModal.vue'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import { Badge } from '@/components/ui/badge'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import {
    FILE_INPUT_ACCEPT,
    isFileSupported,
    SUPPORTED_FILE_TYPES_DISPLAY
  } from '@/constants/documentFileTypes'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'
  import { ROUTES } from '@/router/routes.ts'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    type CaseCreateRequest,
    type CaseCreateEntityWithRelationshipsRequest,
    type CaseCreatorTempDocument,
    CaseCreatorTempDocumentsService,
    CasesService,
    CancelError,
    type CustomerEntity,
    type DeduplicatedCaseCreatorEntity,
    EntityTypeEnum,
    FlagEnum,
    RelationshipTypeEnum,
    type LawAugmentedFieldsRequest,
    type CustomerUser as User,
    type UUIDCaseCreatorEntity,
    type UUIDExtractedLawData
  } from '@/types/generated'
  import {
    ArrowLeft,
    Building2,
    Ellipsis,
    Eye,
    Landmark,
    Loader2,
    Scale,
    Trash2,
    Upload,
    UserIcon,
    UserPlus,
    Users,
    X
  } from 'lucide-vue-next'
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useRouter } from 'vue-router'

  interface UploadingFile {
    file: File
    progress: number
    uploading: boolean
    uploadPhase: 'uploading' | 'complete' | 'error' | 'analyzing' | 'queued'
    error?: string
    document?: CaseCreatorTempDocument
  }

  const { toast } = useToast()
  const { t } = useTranslation({
    en: {
      back: 'Back to all Cases',
      browse: 'browse',
      caseData: 'Case Data',
      client: 'Client',
      createNewCase: 'New Case',
      delete: 'Delete',
      documents: 'Documents Upload',
      documentsUploadHintWithAi: 'Upload your documents (PDF or images) for the Case here.',
      documentsUploadHint:
        'Upload your documents (PDF or images) for the Case here. JUPUS document AI will automatically extract all the relevant data for you.',
      documentsUploadUpsell:
        'Did you know? JUPUS AI can now also automatically extract parties from documents.',
      documentsUploadUpsellLink: 'More information here',
      fileTypeHint: `Allowed files: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}. Maximum size: 100MB.`,
      fileTypeNotSupported: `File type not supported. Supported formats: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}.`,
      fileTooLarge: 'File is too large. Maximum size is 100MB.',
      dragAndDrop: 'Drag and drop files here',
      inquiry: 'Inquiry',
      or: 'or',
      save: 'Save',
      clerk: 'Clerk',
      lawField: 'Law Field',
      lawTopic: 'Law Topic',
      internalNotes: 'Internal Notes',
      internalNotesPlaceholder: 'Enter your internal notes here...',
      selectClerk: 'Select Clerk',
      selectLawField: 'Select Law Field',
      selectLawTopic: 'Select Law Topic',
      opponent: 'Opponent',
      preview: 'Preview',
      setAsClient: 'Set as Client',
      setAsOpponent: 'Set as Opponent',
      setAsLawTopic: 'Set as Law Topic',
      uploading: 'Uploading...',
      analyzing: 'Analyzing data...',
      complete: 'Complete',
      error: 'Error',
      queued: 'Waiting in queue...',
      activeUploadsRemaining: 'files ahead in queue',
      helpCaseCreate: 'Learn more about creating a case with documents',
      dedupFailedTitle: 'Deduplication failed',
      dedupFailedDescription: 'Some entities could not be deduplicated automatically.'
    },
    de: {
      back: 'Zurück zu allen Anfragen',
      browse: 'Dateiauswahl öffnen',
      caseData: 'Anfrage',
      client: 'Mandant',
      createNewCase: 'Neue Anfrage',
      delete: 'Löschen',
      documents: 'Dokumente hochladen',
      documentsUploadHint: 'Laden Sie hier Ihre Dokumente (PDF oder Bilder) zur Anfrage hoch.',
      documentsUploadHintWithAi:
        'Laden Sie hier Ihre Dokumente (PDF oder Bilder) zur Anfrage hoch. Die JUPUS Dokumenten-KI extrahiert automatisch alle relevanten Daten für Sie.',
      documentsUploadUpsell:
        'Wussten Sie schon? JUPUS kann nun auch Beteiligte automatisch aus Dokumenten extrahieren.',
      documentsUploadUpsellLink: 'Jetzt Demonstration anfordern',
      fileTypeHint: `Erlaubte Dateien: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}. Maximale Größe: 100MB.`,
      fileTypeNotSupported: `Dateityp nicht unterstützt. Unterstützte Formate: ${SUPPORTED_FILE_TYPES_DISPLAY.join(', ')}.`,
      fileTooLarge: 'Datei ist zu groß. Maximale Größe ist 100MB.',
      dragAndDrop: 'Legen Sie Ihre Dateien hier ab',
      inquiry: 'Inhalt der Anfrage',
      or: 'oder',
      save: 'Speichern',
      clerk: 'Sachbearbeiter',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      internalNotes: 'Interne Notizen',
      internalNotesPlaceholder: 'Geben Sie hier ihre internen Notizen ein...',
      selectClerk: 'Sachbearbeiter auswählen',
      selectLawField: 'Rechtsgebiet auswählen',
      selectLawTopic: 'Rechtsthema auswählen',
      opponent: 'Gegner',
      preview: 'Vorschau',
      setAsClient: 'Als Mandant festlegen',
      setAsOpponent: 'Als Gegner festlegen',
      setAsLawTopic: 'Als Rechtsthema festlegen',
      uploading: 'Wird hochgeladen...',
      analyzing: 'Analysiere Daten...',
      complete: 'Abgeschlossen',
      error: 'Fehler',
      queued: 'Wartet in Warteschlange...',
      activeUploadsRemaining: 'Dateien vor dieser Datei',
      helpCaseCreate: 'Mehr über die Anlage von Anfragen mit Dokumenten erfahren',
      dedupFailedTitle: 'Deduplizierung fehlgeschlagen',
      dedupFailedDescription: 'Einige Beteiligte konnten nicht automatisch dedupliziert werden.'
    }
  })

  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()
  const { translateLawTopic } = useLawTopicTranslation()
  const { translateLawField } = useLawFieldTranslation()
  const { fetchUser } = useUserStore()

  const router = useRouter()
  const caseStore = useCaseStore()
  const multipleOpponentsFlag = useFeatureFlag(FlagEnum.MULTIPLE_OPPONENTS)

  const uploadingFiles = ref<UploadingFile[]>([])
  const dropZone = ref<HTMLDivElement>()
  // Add a queue for files waiting to be uploaded
  const uploadQueue = ref<File[]>([])
  // Track the number of active uploads
  const activeUploads = ref<number>(0)
  // Maximum number of simultaneous uploads
  const MAX_SIMULTANEOUS_UPLOADS = 5

  const extractedEntities = ref<UUIDCaseCreatorEntity[]>([])
  const extractedLawData = ref<UUIDExtractedLawData[]>([])
  const selectedExtractedClientEntity = ref<UUIDCaseCreatorEntity | null>(null)
  const selectedExtractedOpponentEntity = ref<UUIDCaseCreatorEntity | null>(null)
  const selectedExtractedLawData = ref<UUIDExtractedLawData | null>(null)

  const extractedToStoreEntityMap = ref<Map<string, string>>(new Map())

  // uploads run up to MAX_SIMULTANEOUS_UPLOADS in parallel. when a newer upload
  // finishes mid-flight we cancel the in-flight request and start fresh, so the
  // server response always reflects every entity collected so far.
  type DedupRequest = ReturnType<
    typeof CaseCreatorTempDocumentsService.caseCreatorTempDocumentsDeduplicateEntitiesCreate
  >
  let inflightDedupRequest: DedupRequest | null = null

  // entities from upload #2 onwards stay here until the dedup endpoint tells
  // us which are genuinely new vs. duplicates of already-stored ones.
  // adding them to the store before dedup completes causes a visible flicker
  // when the dedup pass then removes the duplicates.
  type PendingEntity = {
    extractedEntity: UUIDCaseCreatorEntity
    entityToAdd: {
      entity: Partial<CustomerEntity>
      relationshipType: RelationshipTypeEnum
      sourceDocumentUuid?: string
    }
  }
  let pendingEntitiesToAdd: PendingEntity[] = []

  // files whose progress bars are held at 99% waiting for the dedup pass to
  // finish. once dedup resolves (or fails) we flip them to 100% / complete,
  // so the bar only hits 100 when the data the user actually cares about
  // (deduplicated entities) is in place.
  const filesWaitingForDedup = new Set<UploadingFile>()
  const showClientRelationshipModal = ref(false)
  const clientModalAction = ref<'add' | 'remove' | 'addOpponent'>('add')
  const clientModalRelationshipUuid = ref<string>('')

  const clientFormHasErrors = ref(false)
  const opponentFormHasErrors = ref(false)
  const entitiesFormHasErrors = ref(false)
  const isSaving = ref(false)
  const entitiesData = ref<CaseCreateEntityWithRelationshipsRequest[]>([])

  const currentPreviewFile = ref<File | null>(null)
  const previewOpen = ref(false)

  const user = computed<User>(() => useUserStore().user as User)
  const lawFields = computed(() => useLawFieldStore().lawFields)
  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })
  const lawTopicOptions = computed<Option[]>(() => {
    return getLawTopicOptions(createData.value.law.field)
  })
  const useNewEntitiesFormat = computed(() => {
    return multipleOpponentsFlag.isEnabled.value && user.value?.org.enable_law_specific_data
  })

  const isFormValid = computed(() => {
    if (useNewEntitiesFormat.value) {
      return !entitiesFormHasErrors.value
    }
    return !clientFormHasErrors.value && !opponentFormHasErrors.value
  })

  const clerkOptions = computed<Option[]>(() => {
    return user.value.org.users.map((user) => ({
      value: user.uuid,
      title: `${user.first_name} ${user.last_name}`
    }))
  })

  const previewUrl = computed(() => {
    if (!currentPreviewFile.value) return ''

    // For uploaded files, use the server URL if available
    const uploadingFile = uploadingFiles.value.find((f) => f.file === currentPreviewFile.value)
    if (uploadingFile?.document?.file) {
      return uploadingFile.document.file
    }

    // Fallback to local file blob URL for files not yet uploaded
    return URL.createObjectURL(currentPreviewFile.value)
  })

  const previewDocument = computed(() => {
    if (!currentPreviewFile.value) return undefined

    return {
      filesize: currentPreviewFile.value.size,
      page_count: undefined
    }
  })

  const createData = ref<CaseCreateRequest>({
    clerk_uuid: null,
    inquiry: '',
    note: '',
    client: {
      title: '',
      specific_title: '',
      entity_type: EntityTypeEnum.NATURAL_PERSON,
      first_name: '',
      last_name: '',
      company_name: '',
      company_legal_form: '',
      institution_name: '',
      phone: '',
      email: '',
      date_of_birth: null,
      street: '',
      street_number: '',
      address_line_2: '',
      zip: '',
      city: '',
      legal_insurance: null,
      legal_insurance_number: '',
      legal_insurance_name: ''
    },
    opponent: {
      title: '',
      specific_title: '',
      entity_type: EntityTypeEnum.NATURAL_PERSON,
      first_name: '',
      last_name: '',
      company_name: '',
      company_legal_form: '',
      institution_name: '',
      phone: '',
      email: '',
      date_of_birth: null,
      street: '',
      street_number: '',
      address_line_2: '',
      zip: '',
      city: ''
    },
    law: {
      field: '',
      topic: ''
    },
    document_uuids: [],
    ai_augmented_fields: {
      law: {
        field: null,
        topic: null
      },
      client: {
        entity_type: null,
        title: null,
        specific_title: null,
        first_name: null,
        last_name: null,
        company_name: null,
        company_legal_form: null,
        institution_name: null,
        phone: null,
        email: null,
        date_of_birth: null,
        street: null,
        street_number: null,
        address_line_2: null,
        zip: null,
        city: null,
        legal_insurance: null,
        legal_insurance_number: null,
        legal_insurance_name: null
      },
      opponent: {
        entity_type: null,
        title: null,
        specific_title: null,
        first_name: null,
        last_name: null,
        company_name: null,
        company_legal_form: null,
        institution_name: null,
        phone: null,
        email: null,
        date_of_birth: null,
        street: null,
        street_number: null,
        address_line_2: null,
        zip: null,
        city: null,
        legal_insurance: null,
        legal_insurance_number: null,
        legal_insurance_name: null
      }
    }
  })

  const getPhaseTranslation = (phase: string) => {
    return t(phase)
  }

  const formatEntityLabel = (entity: UUIDCaseCreatorEntity) => {
    switch (entity.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON:
        return `${entity.first_name} ${entity.last_name}`
      case EntityTypeEnum.LEGAL_PERSON:
        return `${entity.company_name} ${entity.company_legal_form}`
      case EntityTypeEnum.INSTITUTION:
        return entity.institution_name
      default:
        return ''
    }
  }

  const getEntityIcon = (type: string) => {
    switch (type) {
      case EntityTypeEnum.NATURAL_PERSON:
        return UserIcon
      case EntityTypeEnum.LEGAL_PERSON:
        return Building2
      case EntityTypeEnum.INSTITUTION:
        return Landmark
      default:
        return UserIcon
    }
  }

  const getMostLikelyClient = (entities: UUIDCaseCreatorEntity[]): UUIDCaseCreatorEntity | null => {
    if (entities.length === 0) return null

    const sorted = [...entities].sort((a, b) => b.client_probability - a.client_probability)
    return sorted[0]
  }

  const getMostLikelyOpponent = (
    entities: UUIDCaseCreatorEntity[],
    excludeEntity?: UUIDCaseCreatorEntity
  ): UUIDCaseCreatorEntity | null => {
    let filtered = entities

    if (excludeEntity) {
      filtered = entities.filter((entity) => entity.uuid !== excludeEntity.uuid)
    }

    if (filtered.length === 0) return null

    const sorted = [...filtered].sort((a, b) => b.opponent_probability - a.opponent_probability)
    return sorted[0]
  }

  const selectLawData = (data: UUIDExtractedLawData, documentUuid: string) => {
    selectedExtractedLawData.value = data
    createData.value.law = {
      ...createData.value.law,
      field: data.law_field ?? '',
      topic: data.law_topic ?? ''
    }
    createData.value.ai_augmented_fields!.law = {
      field: data.law_field ? documentUuid : null,
      topic: data.law_topic ? documentUuid : null
    }
  }

  const clearLawData = () => {
    selectedExtractedLawData.value = null
    createData.value.law = {
      ...createData.value.law,
      field: '',
      topic: ''
    }
    clearAiAugmentedFields('law')
  }

  const getStoreEntityForExtracted = (entity: UUIDCaseCreatorEntity) => {
    if (!caseStore.caseRecord?.data?.entities) return null

    const mappedUuid = extractedToStoreEntityMap.value.get(entity.uuid)
    if (mappedUuid) {
      const byMap = caseStore.caseRecord.data.entities.find((e) => e.uuid === mappedUuid)
      if (byMap) return byMap
    }

    return null
  }

  const hasRelationship = (
    storeEntity: { relationships: { relationship_type: string }[] } | null | undefined,
    relationshipType: RelationshipTypeEnum
  ) => {
    return !!storeEntity?.relationships.some((r) => r.relationship_type === relationshipType)
  }

  const currentClientStoreEntity = computed(() => {
    return (
      caseStore.caseRecord?.data?.entities?.find((e) =>
        e.relationships.some((r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT)
      ) ?? null
    )
  })

  const openClientReplacementModal = (
    action: 'add' | 'remove' | 'addOpponent',
    relationshipUuid: string
  ) => {
    clientModalAction.value = action
    clientModalRelationshipUuid.value = relationshipUuid
    showClientRelationshipModal.value = true
  }

  const handleSelectEntity = (entity: any, target: 'client' | 'opponent', documentUuid: string) => {
    if (useNewEntitiesFormat.value && caseStore.isCreatingCase) {
      const storeEntity = getStoreEntityForExtracted(entity)
      if (!storeEntity) return

      const isAlreadyTargetRole = hasRelationship(
        storeEntity,
        target === 'client'
          ? RelationshipTypeEnum.ENTITIES_CLIENT
          : RelationshipTypeEnum.ENTITIES_OPPONENT
      )
      if (isAlreadyTargetRole) return

      if (target === 'client') {
        const existingClient = currentClientStoreEntity.value
        // If the case already has a different client, route through the modal so the user
        // confirms the swap (and the previous client isn't silently demoted/lost).
        if (existingClient && existingClient.uuid !== storeEntity.uuid) {
          openClientReplacementModal('add', storeEntity.uuid as string)
          return
        }

        const conflictingRelationships = storeEntity.relationships.filter(
          (r) => r.relationship_type !== RelationshipTypeEnum.ENTITIES_CLIENT
        )
        conflictingRelationships.forEach((r) => {
          caseStore.removeEntityRelationshipDuringCreation(
            storeEntity.entity.uuid,
            r.uuid as string
          )
        })
        caseStore.addEntityRelationshipDuringCreation(
          storeEntity.entity.uuid,
          RelationshipTypeEnum.ENTITIES_CLIENT
        )
        return
      }

      const isCurrentlyClient = hasRelationship(storeEntity, RelationshipTypeEnum.ENTITIES_CLIENT)
      if (isCurrentlyClient) {
        // Converting the only client to opponent without a replacement would leave the
        // case without a client. Force the user to pick a new client via the modal.
        openClientReplacementModal('addOpponent', storeEntity.uuid as string)
        return
      }

      // No client conflict: strip OTHER relationships so the entity is cleanly an opponent.
      const otherRelationships = storeEntity.relationships.filter(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OTHER
      )
      otherRelationships.forEach((r) => {
        caseStore.removeEntityRelationshipDuringCreation(storeEntity.entity.uuid, r.uuid as string)
      })
      caseStore.addEntityRelationshipDuringCreation(
        storeEntity.entity.uuid,
        RelationshipTypeEnum.ENTITIES_OPPONENT
      )
      return
    }

    if (target === 'client' && isEntitySetAsOpponent(entity)) {
      handleClear('opponent')
    } else if (target === 'opponent' && isEntitySetAsClient(entity)) {
      handleClear('client')
    }
    selectEntity(entity, target, documentUuid)
  }

  const selectEntity = (entity: any, target: 'client' | 'opponent', documentUuid: string) => {
    const data = target === 'client' ? createData.value.client : createData.value.opponent
    createData.value[target] = {
      ...data,
      entity_type: entity.entity_type ?? EntityTypeEnum.NATURAL_PERSON,
      company_legal_form: entity.company_legal_form ?? '',
      company_name: entity.company_name ?? '',
      institution_name: entity.institution_name ?? '',
      title: entity.title ?? '',
      specific_title: entity.specific_title ?? '',
      first_name: entity.first_name ?? '',
      last_name: entity.last_name ?? '',
      phone: entity.phone ?? '',
      email: entity.email ?? '',
      date_of_birth: entity.date_of_birth ?? null,
      street: entity.street ?? '',
      street_number: entity.street_number ?? '',
      zip: entity.zip ?? '',
      city: entity.city ?? '',
      legal_insurance: entity.legal_insurance ?? null,
      legal_insurance_number: entity.legal_insurance_number ?? '',
      legal_insurance_name: entity.legal_insurance_name ?? ''
    }
    if (target === 'client') {
      selectedExtractedClientEntity.value = entity
    } else {
      selectedExtractedOpponentEntity.value = entity
    }

    createData.value.ai_augmented_fields![target] = {
      entity_type: entity.entity_type ? documentUuid : null,
      company_legal_form: entity.company_legal_form ? documentUuid : null,
      company_name: entity.company_name ? documentUuid : null,
      institution_name: entity.institution_name ? documentUuid : null,
      title: entity.title ? documentUuid : null,
      specific_title: entity.specific_title ? documentUuid : null,
      first_name: entity.first_name ? documentUuid : null,
      last_name: entity.last_name ? documentUuid : null,
      phone: entity.phone ? documentUuid : null,
      email: entity.email ? documentUuid : null,
      date_of_birth: entity.date_of_birth ? documentUuid : null,
      street: entity.street ? documentUuid : null,
      street_number: entity.street_number ? documentUuid : null,
      address_line_2: entity.address_line_2 ? documentUuid : null,
      zip: entity.zip ? documentUuid : null,
      city: entity.city ? documentUuid : null,
      legal_insurance: documentUuid,
      legal_insurance_number: entity.legal_insurance_number ? documentUuid : null,
      legal_insurance_name: entity.legal_insurance_name ? documentUuid : null
    }
  }

  const isAiFilled = (key: keyof LawAugmentedFieldsRequest) => {
    if (!createData.value.ai_augmented_fields) return false
    const aiFilled = createData.value.ai_augmented_fields.law
    return aiFilled[key] !== null
  }

  const isEntitySetAsClient = (entity: UUIDCaseCreatorEntity) => {
    if (useNewEntitiesFormat.value && caseStore.isCreatingCase) {
      return hasRelationship(
        getStoreEntityForExtracted(entity),
        RelationshipTypeEnum.ENTITIES_CLIENT
      )
    }
    return selectedExtractedClientEntity.value?.uuid === entity.uuid
  }

  const isEntitySetAsOpponent = (entity: UUIDCaseCreatorEntity) => {
    if (useNewEntitiesFormat.value && caseStore.isCreatingCase) {
      return hasRelationship(
        getStoreEntityForExtracted(entity),
        RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    }
    return selectedExtractedOpponentEntity.value?.uuid === entity.uuid
  }

  const isLawDataSelected = (lawData: UUIDExtractedLawData) => {
    return selectedExtractedLawData.value?.uuid === lawData.uuid
  }

  const handleClientEntityTypeChange = (type: EntityTypeEnum) => {
    if (createData.value.client) {
      createData.value.client.entity_type = type
    }
  }

  const handleOpponentEntityTypeChange = (type: EntityTypeEnum) => {
    if (createData.value.opponent) {
      createData.value.opponent.entity_type = type
    }
  }

  const handleLawFieldTopicChange = () => {
    if (createData.value.ai_augmented_fields) {
      createData.value.ai_augmented_fields.law.field = null
      createData.value.ai_augmented_fields.law.topic = null
    }
  }

  const openFileInput = () => {
    const fileInput = document.querySelector('#fileInput') as HTMLInputElement
    if (fileInput) {
      fileInput.click()
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const getThumbnailUrl = (file: UploadingFile) => {
    return file.document?.images?.find((img) => img.image_preset === 'thumbnail')?.url
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (files) {
      handleFiles(Array.from(files))
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  const validateFile = (file: File): boolean => {
    if (!isFileSupported(file)) {
      toast({
        variant: 'destructive',
        title: t('fileTypeNotSupported')
      })
      return false
    }
    if (file.size > 100 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: t('fileTooLarge'),
        description: file.name
      })
      return false
    }
    return true
  }

  const handleFiles = (files: File[]) => {
    // Filter valid files first
    const validFiles = Array.from(files).filter((file) => validateFile(file))

    // Create entries for all files immediately, with queued status for those not starting right away
    validFiles.forEach((file) => {
      const fileEntry = reactive<UploadingFile>({
        file,
        progress: 0,
        uploading: true,
        uploadPhase: 'queued'
      })
      uploadingFiles.value.push(fileEntry)
    })

    // Add files to the queue
    uploadQueue.value.push(...validFiles)

    // Start processing the queue
    processQueue()
  }

  // Process the upload queue
  const processQueue = () => {
    // Continue uploading files until we reach the maximum or the queue is empty
    while (activeUploads.value < MAX_SIMULTANEOUS_UPLOADS && uploadQueue.value.length > 0) {
      const file = uploadQueue.value.shift()
      if (file) {
        startUpload(file)
      }
    }
  }

  // Function to start upload for a file
  const startUpload = (file: File) => {
    // Find the file entry that was created during handleFiles
    const fileEntry = uploadingFiles.value.find((f) => f.file === file)
    if (!fileEntry) return

    // Update the status to uploading
    fileEntry.uploadPhase = 'uploading'

    // Increment active uploads counter
    activeUploads.value++

    // Start the upload process
    uploadFile(fileEntry)
  }

  // map every input source uuid to the canonical (surviving) uuid it merged into.
  const buildSourceToCanonicalMap = (
    deduplicated: DeduplicatedCaseCreatorEntity[]
  ): Map<string, string> => {
    const sourceToCanonical = new Map<string, string>()
    for (const survivor of deduplicated) {
      for (const src of survivor.source_entity_uuids ?? [survivor.uuid]) {
        sourceToCanonical.set(src, survivor.uuid)
      }
    }
    return sourceToCanonical
  }

  // for each merged group, adopt the first store entity we see and schedule
  // the rest for removal. returns the rebuilt extracted→store map plus the
  // store uuids that lost their canonical slot.
  const reconcileStoreEntities = (
    sourceToCanonical: Map<string, string>
  ): { storeUuidsToRemove: string[]; rebuiltMap: Map<string, string> } => {
    const adoptedByCanonical = new Map<string, string>()
    const storeUuidsToRemove: string[] = []

    for (const [extractedUuid, storeUuid] of extractedToStoreEntityMap.value.entries()) {
      const canonicalUuid = sourceToCanonical.get(extractedUuid)
      if (!canonicalUuid) continue
      const adopted = adoptedByCanonical.get(canonicalUuid)
      if (adopted === undefined) {
        adoptedByCanonical.set(canonicalUuid, storeUuid)
      } else if (adopted !== storeUuid) {
        storeUuidsToRemove.push(storeUuid)
      }
    }

    const rebuiltMap = new Map<string, string>()
    for (const extractedUuid of extractedToStoreEntityMap.value.keys()) {
      const canonicalUuid = sourceToCanonical.get(extractedUuid)
      if (!canonicalUuid) continue
      const storeUuid = adoptedByCanonical.get(canonicalUuid)
      if (storeUuid) rebuiltMap.set(extractedUuid, storeUuid)
    }

    return { storeUuidsToRemove, rebuiltMap }
  }

  const runDedup = async (): Promise<void> => {
    if (extractedEntities.value.length === 0) return

    // abort the previous request so we don't burn an LLM round-trip on a stale snapshot.
    if (inflightDedupRequest !== null) {
      inflightDedupRequest.cancel()
    }

    const request =
      CaseCreatorTempDocumentsService.caseCreatorTempDocumentsDeduplicateEntitiesCreate({
        requestBody: { entities: [...extractedEntities.value] }
      })
    inflightDedupRequest = request

    try {
      const response = await request
      // a newer upload superseded us between resolve and here, drop this result.
      if (inflightDedupRequest !== request) return

      const deduplicated = response?.deduplicated_entities ?? []
      const sourceToCanonical = buildSourceToCanonicalMap(deduplicated)
      const { storeUuidsToRemove, rebuiltMap } = reconcileStoreEntities(sourceToCanonical)

      // dedup only affects the Beteiligte panel (via the case store). the
      // per-file chips keep rendering the original extractedEntities list so
      // each document still shows what was extracted from it.
      if (storeUuidsToRemove.length > 0) {
        caseStore.removeEntitiesFromCreation(storeUuidsToRemove)
      }

      flushPendingEntities(sourceToCanonical, rebuiltMap)
      extractedToStoreEntityMap.value = rebuiltMap
      finalizeFilesWaitingForDedup()
    } catch (err) {
      // cancelled by a newer dedup; that newer call will finalize the held
      // progress bars when it resolves, so leave them alone here.
      if (err instanceof CancelError) return
      // don't block case creation on dedup failure; the user can finish with
      // any duplicates still in the list and clean them up after.
      // fall back to adding the pending entities directly so the user doesn't
      // lose them just because dedup failed.
      console.error('Entity deduplication failed', err)
      flushPendingEntitiesUnconditional()
      finalizeFilesWaitingForDedup()
      toast({
        variant: 'destructive',
        title: t('dedupFailedTitle'),
        description: t('dedupFailedDescription')
      })
    } finally {
      if (inflightDedupRequest === request) {
        inflightDedupRequest = null
      }
    }
  }

  const markFileComplete = (fileEntry: UploadingFile) => {
    fileEntry.progress = 100
    fileEntry.uploadPhase = 'complete'
    fileEntry.uploading = false
  }

  const finalizeFilesWaitingForDedup = () => {
    filesWaitingForDedup.forEach(markFileComplete)
    filesWaitingForDedup.clear()
  }

  // resolves pending entries against the dedup result. when a pending entity's
  // canonical is already in the store (typically a file-1 entry), the pending
  // entry just gets mapped onto that existing store uuid. when several pending
  // entries share a canonical that isn't in the store yet, one becomes the rep
  // that goes through batchAddEntities and the others point at its fresh uuid.
  // rebuiltMap is mutated in place; the caller assigns it back into the ref.
  const flushPendingEntities = (
    sourceToCanonical: Map<string, string>,
    rebuiltMap: Map<string, string>
  ) => {
    if (pendingEntitiesToAdd.length === 0) return

    const storeUuidByCanonical = new Map<string, string>()
    for (const [extractedUuid, storeUuid] of rebuiltMap.entries()) {
      const canonical = sourceToCanonical.get(extractedUuid)
      if (canonical && !storeUuidByCanonical.has(canonical)) {
        storeUuidByCanonical.set(canonical, storeUuid)
      }
    }

    const repByCanonical = new Map<string, PendingEntity>()
    const followers: PendingEntity[] = []

    for (const pending of pendingEntitiesToAdd) {
      const canonical =
        sourceToCanonical.get(pending.extractedEntity.uuid) ?? pending.extractedEntity.uuid
      const existing = storeUuidByCanonical.get(canonical)
      if (existing) {
        rebuiltMap.set(pending.extractedEntity.uuid, existing)
      } else if (repByCanonical.has(canonical)) {
        followers.push(pending)
      } else {
        repByCanonical.set(canonical, pending)
      }
    }

    const reps = [...repByCanonical.values()]
    if (reps.length > 0) {
      const created = caseStore.batchAddEntities(reps.map((p) => p.entityToAdd)) ?? []
      reps.forEach((pending, index) => {
        const storeEntity = created[index]
        if (!storeEntity?.uuid) return
        rebuiltMap.set(pending.extractedEntity.uuid, storeEntity.uuid)
        const canonical =
          sourceToCanonical.get(pending.extractedEntity.uuid) ?? pending.extractedEntity.uuid
        storeUuidByCanonical.set(canonical, storeEntity.uuid)
      })
    }

    for (const follower of followers) {
      const canonical =
        sourceToCanonical.get(follower.extractedEntity.uuid) ?? follower.extractedEntity.uuid
      const storeUuid = storeUuidByCanonical.get(canonical)
      if (storeUuid) {
        rebuiltMap.set(follower.extractedEntity.uuid, storeUuid)
      }
    }

    pendingEntitiesToAdd = []
  }

  // fallback when dedup fails: add everything that was waiting, duplicates and all.
  // the user can clean up manually rather than losing entities entirely.
  const flushPendingEntitiesUnconditional = () => {
    if (pendingEntitiesToAdd.length === 0) return
    const created = caseStore.batchAddEntities(pendingEntitiesToAdd.map((p) => p.entityToAdd)) ?? []
    pendingEntitiesToAdd.forEach((pending, index) => {
      const storeEntity = created[index]
      if (storeEntity?.uuid) {
        extractedToStoreEntityMap.value.set(pending.extractedEntity.uuid, storeEntity.uuid)
      }
    })
    pendingEntitiesToAdd = []
  }

  const uploadFile = async (fileEntry: UploadingFile) => {
    let progressInterval: number
    let slowProgressInterval: number

    // Start progress animation for approximately 10 seconds
    // We'll update more frequently at the start and slow down towards the end
    // to give a more realistic feel of file upload progress
    // Fast progress up to 90%
    progressInterval = window.setInterval(() => {
      if (fileEntry.progress < 30) {
        fileEntry.progress += 3
      } else if (fileEntry.progress < 60) {
        fileEntry.progress += 2
      } else if (fileEntry.progress < 90) {
        fileEntry.progress += 1
        if (fileEntry.progress >= 60) {
          fileEntry.uploadPhase = 'analyzing'
        }
      }
    }, 200)

    // Slow progress after 90% (1% per second)
    slowProgressInterval = window.setInterval(() => {
      if (fileEntry.progress >= 90 && fileEntry.progress < 99) {
        fileEntry.progress += 1
      }
    }, 1000)

    try {
      const formData = {
        file: fileEntry.file,
        original_filename: fileEntry.file.name
      }

      const response = await CaseCreatorTempDocumentsService.caseCreatorTempDocumentsCreate({
        formData
      })

      // Handle extracted_entities as an array even though type is wrong
      const entities = Array.isArray(response.extraction_result.extracted_entities)
        ? response.extraction_result.extracted_entities
        : [response.extraction_result.extracted_entities]
      extractedEntities.value.push(...entities)
      createData.value.document_uuids.push(response.uuid)

      caseStore.registerUploadedDocument(response)

      if (response.extraction_result.extracted_law_data) {
        extractedLawData.value.push(response.extraction_result.extracted_law_data)
      }

      const currentFileEntities = extractedEntities.value.filter(
        (entity) => entity.document_uuid === response.uuid
      )

      if (currentFileEntities.length > 0) {
        // If multiple opponents flag is enabled, batch add entities
        if (useNewEntitiesFormat.value && caseStore.isCreatingCase) {
          // Sort entities by probability to get the best client and opponent
          const sortedEntities = [...currentFileEntities].sort(
            (a, b) => b.client_probability - a.client_probability
          )

          let hasAddedClient = false

          const entitiesToAdd = sortedEntities.map((extractedEntity) => {
            // ONE client (highest client_probability > 50), multiple opponents (>= 80 confidence)
            let relationshipType: RelationshipTypeEnum

            if (extractedEntity.client_probability > 50 && !hasAddedClient) {
              relationshipType = RelationshipTypeEnum.ENTITIES_CLIENT
              hasAddedClient = true
            } else if (extractedEntity.opponent_probability >= 80) {
              relationshipType = RelationshipTypeEnum.ENTITIES_OPPONENT
            } else {
              relationshipType = RelationshipTypeEnum.ENTITIES_OTHER
            }

            return {
              entity: {
                entity_type: extractedEntity.entity_type as EntityTypeEnum,
                title: (extractedEntity.title || '') as any,
                first_name: extractedEntity.first_name || '',
                last_name: extractedEntity.last_name || '',
                company_name: extractedEntity.company_name || '',
                company_legal_form: (extractedEntity.company_legal_form || '') as any,
                institution_name: extractedEntity.institution_name || '',
                phone: extractedEntity.phone || '',
                email: extractedEntity.email || '',
                date_of_birth: extractedEntity.date_of_birth || null,
                street: extractedEntity.street || '',
                street_number: extractedEntity.street_number || '',
                address_line_2: extractedEntity.address_line_2 || '',
                zip: extractedEntity.zip || '',
                city: extractedEntity.city || ''
              },
              relationshipType,
              sourceDocumentUuid: response.uuid
            }
          })

          // first document goes straight into the store; nothing to dedup against yet.
          // subsequent documents stash their entitiesToAdd and let runDedup decide
          // which are genuinely new (avoids the add-then-remove flicker when the
          // file re-extracts entities that are already in the store).
          if (createData.value.document_uuids.length === 1) {
            const createdEntities = caseStore.batchAddEntities(entitiesToAdd) ?? []
            sortedEntities.forEach((extractedEntity, index) => {
              const storeEntity = createdEntities[index]
              if (storeEntity?.uuid) {
                extractedToStoreEntityMap.value.set(extractedEntity.uuid, storeEntity.uuid)
              }
            })
          } else {
            sortedEntities.forEach((extractedEntity, index) => {
              pendingEntitiesToAdd.push({
                extractedEntity,
                entityToAdd: entitiesToAdd[index]
              })
            })
            filesWaitingForDedup.add(fileEntry)
            void runDedup()
          }
        } else {
          // Legacy behavior: select client and opponent
          if (!selectedExtractedClientEntity.value) {
            const mostLikelyClient = getMostLikelyClient(currentFileEntities)
            if (mostLikelyClient) {
              selectEntity(mostLikelyClient, 'client', response.uuid)
            }
          }

          if (!selectedExtractedOpponentEntity.value) {
            const mostLikelyOpponent = getMostLikelyOpponent(
              currentFileEntities,
              selectedExtractedClientEntity.value || undefined
            )
            if (mostLikelyOpponent) {
              selectEntity(mostLikelyOpponent, 'opponent', response.uuid)
            }
          }
        }
      }

      if (!selectedExtractedLawData.value && extractedLawData.value.length > 0) {
        selectLawData(extractedLawData.value[0], response.uuid)
      }

      window.clearInterval(progressInterval)
      window.clearInterval(slowProgressInterval)
      fileEntry.document = response

      // if dedup is going to run for this file, the progress bar stays at 99%
      // until that finishes — see runDedup for the matching finalize call.
      if (filesWaitingForDedup.has(fileEntry)) {
        fileEntry.progress = 99
        fileEntry.uploadPhase = 'analyzing'
      } else {
        markFileComplete(fileEntry)
      }

      // Decrement active uploads counter
      activeUploads.value--

      // Process the next item in the queue
      processQueue()
    } catch (error) {
      window.clearInterval(progressInterval)
      window.clearInterval(slowProgressInterval)
      fileEntry.uploadPhase = 'error'
      fileEntry.error = 'Upload failed'
      fileEntry.uploading = false
      fileEntry.progress = 0

      // Decrement active uploads counter even on error
      activeUploads.value--

      // Process the next item in the queue
      processQueue()
    }
  }

  const getQueuePosition = (file: UploadingFile) => {
    if (!file || file.uploadPhase !== 'queued') return 0

    const queuedFiles = (uploadingFiles.value || []).filter((f) => f.uploadPhase === 'queued')

    // Find the index of this file in the queued files
    const index = queuedFiles.findIndex((f) => f.file.name === file.file.name)

    // Return position (1-based)
    return index >= 0 ? index + 1 : 0
  }

  const previewFile = (file: File) => {
    currentPreviewFile.value = file

    if (file.type === 'application/pdf') {
      previewOpen.value = true
      return
    }

    const uploadingFile = uploadingFiles.value.find((f) => f.file === file)
    if (!uploadingFile?.document?.images) return

    const largePreview = uploadingFile.document.images.find((img) => img.image_preset === 'large')
    if (!largePreview) return
    previewOpen.value = true
  }

  const removeFile = async (file: UploadingFile) => {
    if (!file.document?.uuid) return

    try {
      // Check if this file was used for client or opponent or law data before removing
      const wasUsedForClient = Object.values(
        createData.value.ai_augmented_fields?.client || {}
      ).some((value) => value === file.document?.uuid)
      const wasUsedForOpponent = Object.values(
        createData.value.ai_augmented_fields?.opponent || {}
      ).some((value) => value === file.document?.uuid)
      const wasUsedForLaw = Object.values(createData.value.ai_augmented_fields?.law || {}).some(
        (value) => value === file.document?.uuid
      )

      // Destroy the temp document
      await CaseCreatorTempDocumentsService.caseCreatorTempDocumentsDestroy({
        uuid: file.document?.uuid
      })

      // Clear AI augmented fields if the file was used
      if (wasUsedForClient) {
        clearAiAugmentedFields('client')
      }
      if (wasUsedForOpponent) {
        clearAiAugmentedFields('opponent')
      }
      if (wasUsedForLaw) {
        clearAiAugmentedFields('law')
      }

      // Remove the file from the list
      const index = uploadingFiles.value.findIndex((f) => f.file === file.file)
      if (index !== -1) {
        uploadingFiles.value.splice(index, 1)
      }

      // Remove the UUID from createData.document_uuids
      const uuidIndex = createData.value.document_uuids.indexOf(file.document?.uuid)
      if (uuidIndex !== -1) {
        createData.value.document_uuids.splice(uuidIndex, 1)
      }

      // Reset the file input to allow re-uploading the same file
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (error) {
      console.error('Error removing file:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove file',
        variant: 'destructive'
      })
    }
  }

  const handleClientInvalid = (isInvalid: boolean) => {
    clientFormHasErrors.value = isInvalid
  }

  const handleOpponentInvalid = (isInvalid: boolean) => {
    opponentFormHasErrors.value = isInvalid
  }

  const handleEntitiesUpdate = (entities: CaseCreateEntityWithRelationshipsRequest[]) => {
    entitiesData.value = entities
  }

  const handleEntitiesInvalid = (hasErrors: boolean) => {
    entitiesFormHasErrors.value = hasErrors
  }

  const handleClear = (target: 'client' | 'opponent' | 'law', entity?: UUIDCaseCreatorEntity) => {
    if (target === 'law') {
      clearLawData()
      return
    }

    // In the new entities format we operate on the actual store entities instead of
    // the legacy local `createData.client`/`createData.opponent` mirrors.
    if (useNewEntitiesFormat.value && caseStore.isCreatingCase) {
      if (target === 'client') {
        const client = currentClientStoreEntity.value
        if (!client) return
        // Removing the only client would leave the case without one; route through the
        // modal so the user chooses a replacement before the client is cleared.
        openClientReplacementModal('remove', client.uuid as string)
        return
      }

      // target === 'opponent': remove the opponent relationship from the specific entity
      // the chip belongs to. Fall back to the first opponent if no entity was provided
      // (legacy call sites without entity context).
      const targetStoreEntity = entity
        ? getStoreEntityForExtracted(entity)
        : (caseStore.caseRecord?.data?.entities.find((e) =>
            e.relationships.some(
              (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
            )
          ) ?? null)
      if (!targetStoreEntity) return

      const opponentRelationship = targetStoreEntity.relationships.find(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
      if (opponentRelationship) {
        caseStore.removeEntityRelationshipDuringCreation(
          targetStoreEntity.entity.uuid,
          opponentRelationship.uuid as string
        )
      }
      return
    }

    // Legacy behavior (feature flag off) - unchanged.
    if (target === 'client') {
      selectedExtractedClientEntity.value = null
    } else if (target === 'opponent') {
      selectedExtractedOpponentEntity.value = null
    }

    createData.value[target] = {
      title: '',
      entity_type: EntityTypeEnum.NATURAL_PERSON,
      first_name: '',
      last_name: '',
      company_name: '',
      company_legal_form: '',
      institution_name: '',
      phone: '',
      email: '',
      date_of_birth: null,
      street: '',
      street_number: '',
      zip: '',
      city: '',
      legal_insurance: null,
      legal_insurance_number: '',
      legal_insurance_name: ''
    }

    clearAiAugmentedFields(target)

    clientFormHasErrors.value = false
    opponentFormHasErrors.value = false
  }

  const clearAiAugmentedFields = (target: 'client' | 'opponent' | 'law') => {
    if (!createData.value.ai_augmented_fields) return

    if (target === 'law') {
      createData.value.ai_augmented_fields.law = {
        field: null,
        topic: null
      }
      return
    }

    createData.value.ai_augmented_fields[target] = {
      entity_type: null,
      company_legal_form: null,
      company_name: null,
      institution_name: null,
      title: null,
      specific_title: null,
      first_name: null,
      last_name: null,
      phone: null,
      email: null,
      date_of_birth: null,
      street: null,
      street_number: null,
      address_line_2: null,
      zip: null,
      city: null,
      legal_insurance: null,
      legal_insurance_number: null,
      legal_insurance_name: null
    }
  }

  const navigateToCases = (caseUuid: string = '') => {
    // TODO: Redirect to cases list page once the new cases list view is ready
    if (!caseUuid) return window.location.assign('/cases/')
    window.location.assign(ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', caseUuid))
  }

  const handleSave = async () => {
    if (!isFormValid.value) {
      console.error('Form has validation errors')
      return
    }

    isSaving.value = true
    try {
      const requestBody: CaseCreateRequest = useNewEntitiesFormat.value
        ? ({
            clerk_uuid: createData.value.clerk_uuid,
            entities: entitiesData.value,
            law: createData.value.law,
            inquiry: createData.value.inquiry,
            note: createData.value.note,
            document_uuids: createData.value.document_uuids,
            ai_augmented_fields: {
              law: createData.value.ai_augmented_fields?.law
            }
          } as CaseCreateRequest)
        : {
            ...createData.value,
            client: createData.value.client!,
            opponent: createData.value.opponent!
          }

      const response = await CasesService.casesCreate({ requestBody })
      navigateToCases(response.uuid)
    } catch (error) {
      console.error('Error saving case:', error)
      isSaving.value = false
    }
  }

  onMounted(async () => {
    const fetching = [fetchUser(), fetchLawFields(), multipleOpponentsFlag.checkFlag()]
    await Promise.all(fetching)
  })
</script>

<template>
  <SpaCompatComponent
    v-if="user && lawFields"
    class="min-h-[calc(100vh-60px)] py-4"
  >
    <div
      class="flex cursor-pointer items-center gap-2 text-gray-500 underline"
      @click="navigateToCases()"
    >
      <ArrowLeft class="h-4 w-4" />
      {{ t('back') }}
    </div>
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex items-center gap-2 py-5">
        <h1 class="text-lg font-semibold text-gray-800 md:text-xl">{{ t('createNewCase') }}</h1>
        <HelpLink
          :url="KNOWLEDGE_BASE_URLS.caseCreate"
          :tooltip="t('helpCaseCreate')"
          location="case_create"
        />
      </div>
      <button
        :disabled="!isFormValid || isSaving"
        class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm
          font-medium text-white transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400
          disabled:opacity-50"
        data-cy="save-button"
        @click="handleSave"
      >
        <Loader2
          v-if="isSaving"
          class="h-4 w-4 animate-spin"
        />
        {{ t('save') }}
      </button>
    </div>

    <!-- Document Upload Widget -->
    <div class="mb-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
      <div class="mb-3 flex flex-col">
        <h2 class="text-lg font-medium text-gray-900">{{ t('documents') }}</h2>
        <p class="text-gray-500">
          {{
            user.org.enable_law_specific_data
              ? t('documentsUploadHintWithAi')
              : t('documentsUploadHint')
          }}
        </p>
      </div>
      <!-- Upload Widget -->
      <div
        ref="dropZone"
        class="relative flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2
          border-dashed border-gray-300 bg-white p-6 hover:border-gray-400"
        @dragenter.prevent
        @dragover.prevent
        @dragleave.prevent
        @drop="onDrop"
        @click="openFileInput"
      >
        <div class="flex flex-col items-center justify-center pb-6 pt-5">
          <Upload class="mb-3 h-10 w-10 text-gray-400" />
          <p class="mb-2 text-sm text-gray-500">
            {{ t('dragAndDrop') }} {{ t('or') }}
            <span class="font-semibold text-purple-600">{{ t('browse') }}</span>
          </p>
          <p class="text-xs text-gray-500">{{ t('fileTypeHint') }}</p>
        </div>
        <input
          id="fileInput"
          type="file"
          multiple
          class="hidden"
          data-cy="file-input"
          :accept="FILE_INPUT_ACCEPT"
          @change="onFileInput"
        />
      </div>
      <p
        v-if="!user.org.enable_law_specific_data"
        class="pt-1 text-sm text-gray-500"
      >
        {{ t('documentsUploadUpsell') }}
        <a
          href="https://eu1.hubs.ly/H0hgDHY0"
          target="_blank"
          class="text-purple-600"
        >
          {{ t('documentsUploadUpsellLink') }}
        </a>
        .
      </p>
      <!-- Uploaded Files List -->
      <div
        v-if="uploadingFiles.length > 0"
        class="mt-3 space-y-3"
      >
        <div
          v-for="file in uploadingFiles"
          :key="file.file.name"
          class="relative rounded-lg border bg-white p-4 shadow-sm"
          data-cy="uploaded-file-item"
          :data-upload-status="file.uploadPhase"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start space-x-3">
              <div
                v-if="getThumbnailUrl(file)"
                class="h-10 w-10 flex-shrink-0"
              >
                <img
                  :src="getThumbnailUrl(file)"
                  class="h-full w-full rounded object-cover"
                />
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">{{ file.file.name }}</p>
                <p class="text-sm text-gray-500">{{ formatFileSize(file.file.size) }}</p>
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <button
                v-if="!file.uploading"
                type="button"
                class="flex items-center gap-1 rounded-md bg-white px-2 py-1 shadow-sm ring-1 ring-gray-900/5
                  hover:bg-gray-50"
                @click="previewFile(file.file)"
              >
                <Eye class="h-5 w-5" />
                {{ t('preview') }}
              </button>
              <button
                v-if="!file.uploading"
                type="button"
                class="flex items-center gap-1 rounded-md bg-white px-2 py-1 text-red-500 shadow-sm ring-1 ring-gray-900/5
                  hover:bg-gray-50"
                @click="removeFile(file)"
              >
                <Trash2 class="h-5 w-5" />
                {{ t('delete') }}
              </button>
            </div>
          </div>

          <!-- Progress Bar -->
          <div
            v-if="file.uploading"
            class="mt-2 space-y-2"
            data-cy="upload-progress"
          >
            <div class="flex items-center justify-between text-xs text-gray-500">
              <span>{{ getPhaseTranslation(file.uploadPhase) }}</span>
              <span v-if="file.uploadPhase !== 'queued'">{{ Math.round(file.progress) }}%</span>
              <span v-else>{{ getQueuePosition(file) }} {{ t('activeUploadsRemaining') }}</span>
            </div>
            <div class="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="{
                  'bg-blue-600':
                    file.uploadPhase === 'uploading' || file.uploadPhase === 'analyzing',
                  'bg-green-500': file.uploadPhase === 'complete',
                  'bg-red-500': file.uploadPhase === 'error',
                  'bg-yellow-500': file.uploadPhase === 'queued'
                }"
                :style="{ width: file.uploadPhase === 'queued' ? '100%' : `${file.progress}%` }"
              />
            </div>
          </div>

          <!-- Error Message -->
          <p
            v-if="file.error"
            class="mt-2 text-sm text-red-600"
          >
            {{ file.error }}
          </p>

          <!-- Extracted Entities -->
          <div
            v-if="(extractedEntities && extractedEntities.length > 0) || extractedLawData"
            class="mt-3 flex items-center justify-between gap-2"
          >
            <div
              v-if="file.document?.uuid"
              class="flex flex-wrap gap-2"
            >
              <Popover
                v-for="entity in extractedEntities.filter(
                  (e) => e.document_uuid === file.document?.uuid
                )"
                :key="entity.uuid"
              >
                <PopoverTrigger
                  class="relative z-10 m-0.5 flex cursor-pointer flex-nowrap items-center rounded-full border border-gray-200
                    bg-white p-[3px] pe-3 hover:bg-gray-50"
                >
                  <div class="me-1 flex size-6 items-center justify-center">
                    <component
                      :is="getEntityIcon(entity.entity_type)"
                      class="h-3.5 w-3.5 text-gray-500"
                    />
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="whitespace-nowrap text-gray-700">
                      {{ formatEntityLabel(entity) }}
                    </div>
                    <div
                      v-if="isEntitySetAsClient(entity) || isEntitySetAsOpponent(entity)"
                      class="text-xs font-medium text-gray-700"
                    >
                      <Badge
                        v-if="isEntitySetAsClient(entity)"
                        class="bg-purple-100 text-purple-600"
                      >
                        {{ t('client') }}
                        <div @click.stop="handleClear('client')">
                          <X class="h-4 w-4" />
                        </div>
                      </Badge>
                      <Badge
                        v-if="isEntitySetAsOpponent(entity)"
                        class="bg-purple-100 text-purple-600"
                      >
                        {{ t('opponent') }}
                        <div @click.stop="handleClear('opponent', entity)">
                          <X class="h-4 w-4" />
                        </div>
                      </Badge>
                    </div>
                    <div v-if="!isEntitySetAsClient(entity) || !isEntitySetAsOpponent(entity)">
                      <Ellipsis class="h-5 w-5" />
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  v-if="!isEntitySetAsClient(entity) || !isEntitySetAsOpponent(entity)"
                  class="w-[--radix-popper-anchor-width] min-w-48 p-2"
                >
                  <div class="flex flex-col gap-1">
                    <button
                      v-if="!isEntitySetAsClient(entity)"
                      class="inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      @click="handleSelectEntity(entity, 'client', file.document?.uuid)"
                    >
                      <UserPlus class="mr-2 h-4 w-4" />
                      {{ t('setAsClient') }}
                    </button>
                    <button
                      v-if="!isEntitySetAsOpponent(entity)"
                      class="inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      @click="handleSelectEntity(entity, 'opponent', file.document?.uuid)"
                    >
                      <Users class="mr-2 h-4 w-4" />
                      {{ t('setAsOpponent') }}
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <Popover
                v-for="lawData in extractedLawData.filter(
                  (e: any) => e.document_uuid === file.document?.uuid
                )"
                :key="lawData.uuid"
              >
                <PopoverTrigger
                  class="relative z-10 m-0.5 flex cursor-pointer flex-nowrap items-center rounded-full border border-gray-200
                    bg-white p-[3px] pe-3 hover:bg-gray-50"
                >
                  <div class="me-1 flex size-6 items-center justify-center">
                    <component
                      :is="Scale"
                      class="h-3.5 w-3.5 text-gray-500"
                    />
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="whitespace-nowrap text-gray-700">
                      {{ translateLawField(lawData?.law_field) }}/{{
                        translateLawTopic(lawData?.law_topic)
                      }}
                    </div>
                    <div
                      v-if="selectedExtractedLawData?.uuid === lawData?.uuid"
                      class="text-xs font-medium text-gray-700"
                    >
                      <Badge class="bg-purple-100 text-purple-600">
                        {{ t('lawTopic') }}
                        <div @click.stop="handleClear('law')">
                          <X class="h-4 w-4" />
                        </div>
                      </Badge>
                    </div>
                    <div v-if="!isLawDataSelected(lawData)">
                      <Ellipsis class="h-5 w-5" />
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  v-if="!isLawDataSelected(lawData)"
                  class="w-[--radix-popper-anchor-width] min-w-48 p-2"
                >
                  <div>
                    <button
                      class="inline-flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      @click="selectLawData(lawData, file.document?.uuid)"
                    >
                      <Scale class="mr-2 h-4 w-4" />
                      {{ t('setAsLawTopic') }}
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Case Information -->
    <div class="mb-8 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
      <h2 class="pb-3 text-lg font-medium text-gray-900">
        {{ t('caseData') }}
      </h2>
      <div class="flex items-center gap-3">
        <!-- Law Field -->
        <div
          class="flex-1"
          :class="isAiFilled('field') ? 'ai-filled' : ''"
        >
          <SelectField
            v-model="createData.law.field"
            name="lawField"
            :label="t('lawField')"
            :options="lawFieldOptions"
            :disabled="isSaving"
            :placeholder="t('selectLawField')"
            :prefilled="isAiFilled('field')"
            @update:model-value="handleLawFieldTopicChange"
          />
        </div>
        <!-- Law Topic -->
        <div
          class="flex-1"
          :class="isAiFilled('topic') ? 'ai-filled' : ''"
        >
          <SelectField
            v-model="createData.law.topic"
            name="lawTopic"
            :label="t('lawTopic')"
            :options="lawTopicOptions"
            :disabled="!createData.law.field || isSaving"
            :placeholder="t('selectLawTopic')"
            :prefilled="isAiFilled('topic')"
            @update:model-value="handleLawFieldTopicChange"
          />
        </div>

        <!-- Clerk -->
        <SelectField
          v-model="createData.clerk_uuid"
          name="clerk"
          class="flex-1"
          :label="t('clerk')"
          :options="clerkOptions"
          :disabled="isSaving"
        />
      </div>
    </div>
    <!-- Entities Section - New format when multiple opponents is enabled -->
    <CaseCreateEntities
      v-if="useNewEntitiesFormat"
      :disabled="isSaving"
      @update:entities="handleEntitiesUpdate"
      @invalid="handleEntitiesInvalid"
    />

    <!-- Legacy Client/Opponent Forms - Old format when multiple opponents is disabled -->
    <div
      v-else
      class="relative mb-8 grid grid-cols-2 gap-6"
    >
      <!-- Client Information -->
      <CaseCreateEntitiesForm
        v-model="createData.client"
        target="client"
        :ai-fields="createData.ai_augmented_fields?.client"
        :disabled="isSaving"
        @update:entity-type-change="handleClientEntityTypeChange"
        @invalid="handleClientInvalid"
        @clear="handleClear"
      />
      <!-- Opponent Information -->
      <CaseCreateEntitiesForm
        v-model="createData.opponent"
        target="opponent"
        :ai-fields="createData.ai_augmented_fields?.opponent"
        :disabled="isSaving"
        @update:entity-type-change="handleOpponentEntityTypeChange"
        @invalid="handleOpponentInvalid"
        @clear="handleClear"
      />
    </div>

    <!-- Inquiry and Notes Section -->
    <div class="relative mb-8 grid grid-cols-2 gap-6">
      <!-- Inquiry -->
      <div
        class="grid-cols2 h-72 space-y-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5"
        data-cy="inquiry-section"
      >
        <h2
          class="text-lg font-medium text-gray-900"
          data-cy="inquiry-section-header"
        >
          {{ t('inquiry') }}
        </h2>
        <TextAreaField
          v-model="createData.inquiry"
          name="internal_notes"
          class="textarea textarea-bordered h-[calc(100%-3rem)]"
          :disabled="isSaving"
          :placeholder="t('internalNotesPlaceholder')"
          :height-full="true"
          data-cy="inquiry-textarea"
        />
      </div>
      <!-- Notes -->
      <div
        class="h-72 grid-cols-2 space-y-4 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5"
        data-cy="internal-notes-section"
      >
        <h2
          class="text-lg font-medium text-gray-900"
          data-cy="internal-notes-section-header"
        >
          {{ t('internalNotes') }}
        </h2>
        <TextAreaField
          v-model="createData.note"
          name="internal_notes"
          class="textarea textarea-bordered h-[calc(100%-3rem)]"
          :disabled="isSaving"
          :placeholder="t('internalNotesPlaceholder')"
          :height-full="true"
          data-cy="internal-notes-textarea"
        />
      </div>
    </div>
  </SpaCompatComponent>
  <PDFViewer
    v-if="currentPreviewFile?.type === 'application/pdf'"
    :url="previewUrl"
    :filename="currentPreviewFile.name"
    :is-open="previewOpen"
    :document="previewDocument"
    @update:is-open="previewOpen = false"
  />
  <Dialog
    v-if="currentPreviewFile && currentPreviewFile.type !== 'application/pdf'"
    v-model:open="previewOpen"
  >
    <DialogContent class="max-h-[95vh] max-w-[45vw] overflow-hidden p-0">
      <div class="relative h-[90vh] w-full overflow-auto">
        <img
          :src="previewUrl"
          class="h-full w-full object-contain"
          alt="Document preview"
        />
      </div>
    </DialogContent>
  </Dialog>

  <ClientRelationshipReplacementModal
    v-if="useNewEntitiesFormat"
    v-model:isOpen="showClientRelationshipModal"
    :action="clientModalAction"
    :relationship-uuid="clientModalRelationshipUuid"
  />
</template>

<style scoped>
  .ai-filled label::after {
    content: 'KI';
    background-color: #e9d5ff !important;
    color: #7e22ce;
    margin-left: 4px;
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 0.75rem;
  }
</style>
